import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { listInstitutes, getInstitute, setInstitute as persistInstitute } from "@/lib/api";

const Ctx = createContext(null);

const FALLBACK_NAME = "Events Platform";

export function InstituteProvider({ children }) {
  const [institutes, setInstitutes] = useState([]);
  const [slug, setSlug] = useState(() => getInstitute());
  const [institute, setInstituteMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshList = useCallback(async () => {
    try {
      const list = await listInstitutes();
      setInstitutes(list);
      return list;
    } catch {
      setInstitutes([]);
      return [];
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await refreshList();
      if (cancelled) return;
      const current = getInstitute();
      const meta = list.find((i) => i.slug === current) || null;
      setSlug(current);
      setInstituteMeta(meta);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refreshList]);

  // Apply branding to the page (CSS vars, title, favicon) whenever it changes.
  useEffect(() => {
    const root = document.documentElement;
    if (institute) {
      root.style.setProperty("--brand-primary", institute.primaryColor || "#0654FC");
      root.style.setProperty("--brand-accent", institute.accentColor || "#1BA85C");
      document.title = institute.shortName ? `${institute.shortName} Events` : `${institute.name} Events`;
      if (institute.faviconUrl) {
        let link = document.querySelector("link[rel='icon']");
        if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
        link.href = institute.faviconUrl;
      }
    } else {
      root.style.removeProperty("--brand-primary");
      root.style.removeProperty("--brand-accent");
      document.title = FALLBACK_NAME;
    }
  }, [institute]);

  const selectInstitute = useCallback((newSlug) => {
    const meta = institutes.find((i) => i.slug === newSlug) || null;
    persistInstitute(newSlug);
    setSlug(newSlug);
    setInstituteMeta(meta);
  }, [institutes]);

  const clearInstitute = useCallback(() => {
    persistInstitute(null);
    setSlug("");
    setInstituteMeta(null);
  }, []);

  const value = {
    institutes,
    institute,
    slug,
    loading,
    hasInstitute: !!slug && !!institute,
    selectInstitute,
    clearInstitute,
    refreshList,
    instituteName: institute?.name || FALLBACK_NAME,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useInstitute = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useInstitute must be inside InstituteProvider");
  return v;
};
